import axios from "axios";
import CryptoJS from "crypto-js";
import {ClassInfo, ImageListType, StudentInfo} from "type/type";
import {getImageUrl} from "../api/api";
import DefaultProfileS from "../assets/image/common/default_profile_S.svg";
import NoImage from "../assets/image/common/no-img.svg";

// ****************************************** 암/복호화 *********************************

export const AESKEY = process.env.REACT_APP_AES_KEY || "";

/**
 * 암호화
 * @param str 암호화할 문자열
 * @param key 암호화에 사용될 키
 * @returns
 */
export function aesCBCEncrypt(str: string): string {
    const key = AESKEY;
    const encryptString = CryptoJS.enc.Utf8.parse(str);
    const secretKey = CryptoJS.enc.Utf8.parse(key);
    const IV = CryptoJS.enc.Utf8.parse(key.substring(0, 16));

    const encrypted = CryptoJS.AES.encrypt(encryptString, secretKey, {
        iv: IV,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.ciphertext.toString(CryptoJS.enc.Hex);
}
/**
 * 복호화
 * @param str 암호화된 문자열
 * @param key 암호화에 사용된 키
 * @returns
 */
export function aesCBCDecrypt(encryptedHexString: string): string {
    const key = AESKEY;
    const secretKey = CryptoJS.enc.Utf8.parse(key);
    const IV = CryptoJS.enc.Utf8.parse(key.substring(0, 16));

    const cipherParams = CryptoJS.lib.CipherParams.create({
        ciphertext: CryptoJS.enc.Hex.parse(encryptedHexString),
    });

    const decrypted = CryptoJS.AES.decrypt(cipherParams, secretKey, {
        iv: IV,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
    });
    // 바이트 배열을 문자열로 디코딩
    return decrypted.toString(CryptoJS.enc.Utf8);
}

// ****************************************** 암/복호화 *********************************
// ****************************************** 공공데이터 *********************************
// 공공데이터(SGIS) token 발급
const API_ACCESS_LOOP_URL =
    "https://sgisapi.kostat.go.kr/OpenAPI3/auth/authentication.json"; // 인증루프 URL
const CONSUMERKEY = process.env.REACT_APP_CONSUMER_KEY;
const CONSUMERSECRET = process.env.REACT_APP_CONSUMER_SECRET;

const HOLIDAY_BASE_URL = "http://apis.data.go.kr/B090041/openapi/service/SpcdeInfoService/getHoliDeInfo";
const HOLIDAY_SERVICE_KEY = process.env.REACT_APP_HOLIDAY_API_KEY; // ~ 2026-12-26

// 도/시 요청
export const API_URL = "https://sgisapi.kostat.go.kr/OpenAPI3/addr/stage.json";

// 4시간짜리 token 발급
export const getAccessToken = async () => {
    let tokenData = await axios.get(API_ACCESS_LOOP_URL, {
        params: {
            consumer_key: CONSUMERKEY,
            consumer_secret: CONSUMERSECRET,
        },
    });
    tokenData = tokenData.data.result.accessToken;
    return tokenData;
};

export const CURRENTYEAR = new Date().getFullYear();

/**
 * 공휴일 데이터
 * @param year 
 * @returns 
 */
export const fetchHolidaysByYear = async (year: string | number = CURRENTYEAR) => {
    try {
        const data = await fetch(`${HOLIDAY_BASE_URL}?solYear=${year}&ServiceKey=${HOLIDAY_SERVICE_KEY}&numOfRows=100&_type=json`);

        const { response } = await data.json();
        const _holidays = response?.body?.items?.item;

        const formatHolidays = (_holidays as any[]).flatMap((item, index) => {
            const date = `${String(item.locdate).substring(0, 4)}-${String(item.locdate).substring(4, 6)}-${String(item.locdate).substring(6)}`;
            return date;
        });
        return formatHolidays;
    } catch (e) {
        // ERROR 처리 or Throw ERROR
        console.error(e);
    }
};

// ****************************************** 공공데이터 *********************************
// ****************************************** 날짜/요일/시간 *********************************

// datepicker에 사용할 월
export const MONTHS = [ "1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월" ];

/**
 * number 요일을 string으로 변경
 * @param day 요일 number
 */
export const changeDayToString = (day: number) => {
    return day === 0 ? "일" : day === 1 ? "월" : day === 2 ? "화" : day === 3 ? "수" : day === 4 ? "목" : day === 5 ? "금" : "토";
};

/**
 * 현재 년월일 -> yyyy-MM-dd
 * @returns string
 */
export const getCurrentYYMMDD = () => {
    const currentDate = new Date()
        .toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        })
        .replaceAll(".", "")
        .replaceAll(" ", "-");
    return currentDate;
};

/**
 * 현재 년월일 -> yyyy년 MM월 dd일 D요일
 * @returns string
 */
export const getCurrentYYMMDDD = () => {
    const currentDate = new Date()
        .toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            weekday: "long",
        })
    return currentDate;
};

/**
 * 현재 시간 yyyy-MM-dd hh:mm:ss
 * @Date prop 
 * @returns string
 */
export const getCurrentDateTime = (prop?: Date): string => {
    let currentDate = new Date();

    if (prop !== undefined) currentDate = prop;

    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
    const day = currentDate.getDate().toString().padStart(2, "0");
    const hours = currentDate.getHours().toString().padStart(2, "0");
    const minutes = currentDate.getMinutes().toString().padStart(2, "0");
    const seconds = currentDate.getSeconds().toString().padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Date는 yyyy-MM-dd 형식으로 변환 + 유효성 체크
 * string 형식이 'yyyy-MM-dd' 이면 유효성 체크
 * string 형식이 'yy-MM-dd', 'yyMMdd' 이면 yyyy-MM-dd로 변환 + 유효성 검사
 * @param date: Date | string('yyyy-MM-dd', 'yy-MM-dd', 'yyMMdd')
 * @returns string
 */
export const checkDateAndFormattedDate = (date: Date | string) => {
    let parsedDate: Date;

    // Date 객체일 경우
    if (date instanceof Date) parsedDate = date;
    else {
        // string 형식 처리
        if (typeof date === "string") {
            // "YYYY-MM-DD"/"YY-MM-DD" 형식 처리
            const currentYYYY = new Date().getFullYear();

            if (date.includes("-")) {
                const parts = date.split("-");
                
                // YYYY-MM-DD 또는 YY-MM-DD 형식인지 체크
                if (parts.length === 3) {
                    // YY인 경우 앞2자리 결정 19xx, 20xx
                    const year = parts[0].length === 2 ? (String(currentYYYY - 100).slice(2) >= parts[0] ? `20${parts[0]}` : `19${parts[0]}`) : parts[0];
                    const month = parts[1];
                    const day = parts[2];

                    parsedDate = new Date(`${year}-${month}-${day}`);
                } else {
                    throw new Error("Invalid date format. Expected 'YYYY-MM-DD' or 'YY-MM-DD'.");
                }
            }
            // "YYMMDD" 형식 처리
            else if (/^\d{6}$/.test(date)) {
                // 앞2자리 결정 19xx, 20xx
                const yy = date.slice(0, 2);
                
                const year = String(currentYYYY - 100).slice(2) >= yy ? `20${yy}` : `19${yy}`;
                const month = date.slice(2, 4);
                const day = date.slice(4, 6);
                
                parsedDate = new Date(`${year}-${month}-${day}`);
            }
            else {
                throw new Error("Invalid date format. Expected 'YYYY-MM-DD', 'YY-MM-DD', or 'YYMMDD'.");
            }
        }
        else {
            throw new Error("Invalid date input. Expected a Date object or a string.");
        }
    }

    // 유효한 날짜인지 확인
    if (isNaN(parsedDate.getTime())) {
        console.error("Invalid date");
    }

    const formatDate = parsedDate
        .toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        })
        .replaceAll(/\./g, "")
        .replaceAll(" ", "-");
    
    return formatDate;
};

/**
 * Date -> yyyy-MM-dd(유효성 검사는 없음)
 * @Date date 
 * @returns string
 */
export const formattedDateHyphenYYYYMMDD = (date: Date | string) => {
    const currentDate = new Date(date)
        .toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        })
        .replaceAll(".", "")
        .replaceAll(" ", "-");
    return currentDate;
};

/**
 * Date -> yyyyMMdd(유효성 검사는 없음)
 * @Date date 
 * @returns string
 */
export const formattedDateYYYYMMDD = (date: Date) => {
    const currentDate = new Date(date)
        .toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        })
        .replaceAll(".", "")
        .replaceAll(" ", "");
    return currentDate;
};

/**
 * yyyy-mm-dd -> yyyy년 mm월 dd일(유효성 검사는 없음)
 * @string dateString 
 * @returns string
 */
export const formatDateToKorean = (dateString: string):string =>{
    let  value = "";
    try{

        const match = dateString.match(/(\d{4})(?:-)?(\d{2})(?:-)?(\d{2})/);

        if (match) {
            const [, year, month, day] = match;
            value = `${year}년 ${month}월 ${day}일`;
        }

    }
    catch(e){
        console.error(e);
    }

    return value;
}

/**
 * 날짜시간 string에 T, Z 제거
 * - ex) 2024-02-20T16:10:25.000Z -> 2024-02-20 16:10:25
 * @string date 
 * @returns 
 */
export const formattedDateTime = (date: string): string => {
    const originalDate = new Date(date);

    originalDate.setHours(originalDate.getHours() - 9);

    const year = originalDate.getFullYear();
    const month = String(originalDate.getMonth() + 1).padStart(2, "0");
    const day = String(originalDate.getDate()).padStart(2, "0");
    const hours = String(originalDate.getHours()).padStart(2, "0");
    const minutes = String(originalDate.getMinutes()).padStart(2, "0");
    const seconds = String(originalDate.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * 시:분:초 -> 시:분 (초 제거)
 * @string time 
 * @returns 
 */
export const formattedHHMM = (time: string): string => {
    return time.substring(0, 5);
};

/**
 * 나이 계산
 * @string birth 
 * @returns number
 */
export const getStudentAge = (birth: string) => {
    const currentDate = new Date();
    const birthDate = new Date(birth);

    // 나이
    let age = currentDate.getFullYear() - birthDate.getFullYear();

    // 생일달이 아직 지나지 않은 경우
    if (birthDate.getMonth() + 1 > currentDate.getMonth() + 1) age = age - 1;
    
    // 생일달인 경우
    else if (birthDate.getMonth() + 1 === currentDate.getMonth() + 1) {
        if (birthDate.getDate() >= currentDate.getDate()) age = age - 1;
        else return age;
    }
    // 생일이 지난 경우
    else return age;
    return age;
};

/**
 * 영업일 계산
 * @param date 
 * @returns number
 */
export function calcWeekday(date: string | Date) {
    const pickMonthYear = new Date(date).getFullYear();
    const pickMonthMonth = new Date(date).getMonth() + 1;
    const pickMonthEndDate = new Date(pickMonthYear, pickMonthMonth, 0).getDate(); // 월 말일

    let weekdayCnt = 0;
    for (let i = 1; i <= pickMonthEndDate; i++) {
        let day = new Date(pickMonthYear, pickMonthMonth - 1, i).getDay(); // 0, 6이면 주말
        if (0 < day && day < 6) weekdayCnt += 1;
    }
    return weekdayCnt;
}

// ****************************************** 날짜/요일/시간 *********************************
// ****************************************** 유효성 검사 *********************************

/**
 * 아이디 유효성 검사
 * 대/소문자/숫자
 * @string id 
 * @returns boolean
 */
export const checkId = (id: string) => {
    const regex = /^[a-zA-Z0-9]+$/g;
    return regex.test(id);
};

/**
 * 이름 유효성 검사
 * 대/소문자/숫자/한글/소괄호()
 * @string name 
 * @returns boolean
 */
export const checkName = (name: string) => {
    const regex = /^[a-zA-Z가-힣0-9()]+$/g;
    return regex.test(name);
};

/**
 * 핸드폰번호 유효성 검사
 * '-'빼고 숫자만 11자리
 * @string mobile 핸드폰번호
 * @returns boolean
 */
export const checkMobile = (mobile: string) => {
    const regex = /^01[0-9]{1}[0-9]{8}$/g;
    return regex.test(mobile);
};

/**
 * 전화번호 유효성 검사
 * '-'빼고 숫자만 9~11자리
 * @string tel 전화번호
 * @returns boolean
 */
export const checkTelephone = (tel: string) => {
  const regex = /^0\d{8,10}$/g;
  return regex.test(tel);
};

/**
 * 과거/미래/현재인지 확인
 * @string date 
 * @returns F 미래, C 현재, P 과거
 */
export const checkFutureDate = (date: string) => {
    if (!date) return '';

    const today = getCurrentYYMMDD();

    if (today < date) return 'F';
    else if (today === date) return 'C';
    else return 'P';
}

/**
 * 유효한 날짜 형식인지 확인하기
 * @param dateString 
 * @returns boolean
 */
export const isValidDate = (dateString: string) => {
    let result = false;

    if (!isNaN(Date.parse(dateString))) {
        const dateSplit = dateString.split('-');
        const year = parseInt(dateSplit[0], 10);
        const month = parseInt(dateSplit[1], 10) - 1; // 월은 0부터 시작
        const day = parseInt(dateSplit[2], 10);
        
        // 날짜 객체 생성
        const checkDate = new Date(year, month, day);
        
        // 날짜가 올바른지 확인
        result = checkDate.getFullYear() === year &&
            checkDate.getMonth() === month &&
            checkDate.getDate() === day;
    }
    return result;
}

/**
 * input 입력 시 생년월일 값 변경 이벤트
 * 자동 '-' 추가
 * @string birth 
 * @returns string
 */
export const formattedBirthDay = (birth: string) => {

    let birthDay = birth.replace(/\D/g, '');
  
    if (birthDay.length > 8) birthDay = birthDay.slice(0, 8);
  
    // '-' 추가
    if (birthDay.length > 4) birthDay = birthDay.slice(0, 4) + '-' + birthDay.slice(4);
    if (birthDay.length > 7) birthDay = birthDay.slice(0, 7) + '-' + birthDay.slice(7);
  
    return birthDay;
  };

// ****************************************** 유효성 검사 *********************************
// ****************************************** 변수 *********************************

export const GOOGLESTORE_URL = "https://play.google.com/store/apps/details?id=com.mFocus.AisarangSchool";
export const APPSTORE_URL = "https://apps.apple.com/kr/app/%EC%95%84%EC%9D%B4%EC%8A%A4%EC%BF%A8-ai-school/id6738344725";

// 사용자(user) - role
export const ROLE_DIRECTOR = "ROLE_DIRECTOR";
export const ROLE_TEACHER = "ROLE_TEACHER";
export const ROLE_PARENT = "ROLE_PARENT";

// 알림장(notebook) - 작성자 타입
export const create_user_type_TEACHER = 1;
export const create_user_type_PARENT = 2;

// ****************************************** 변수 *********************************
// ****************************************** type 관련 *********************************
// 알림장(notebook) - 타입
export const NotebookType = ["행사", "준비물", "수업", "생활", "기타"];
export const changeNotebookType = (type: number) => {
    // (0:미지정, 1:행사, 2:준비물, 3:수업, 4:생활, 5:기타)
    switch (type) {
        case 1:
            return "행사";
        case 2:
            return "준비물";
        case 3:
            return "수업";
        case 4:
            return "생활";
        case 5:
            return "기타";
        default:
            return "기타";
    }
};

// 출석부(attendance) - 출결 타입
// export const AttendanceStatus = ["결석", "출석", "인정 결석", "병결", "사고", "입소", "퇴소"];
export const AttendanceStatus = ["결석", "출석", "인정 결석", "병결", "사고", "조퇴"];
export const AttendanceStatusType = [
  { value: 0, label: "결석" },
  { value: 1, label: "출석" },
  { value: 2, label: "인정 결석" },
  { value: 3, label: "병결" },
  { value: 4, label: "사고" },
  { value: 5, label: "조퇴" },
  // { value: 5, label: "입소" },
  // { value: 6, label: "퇴소" },
];
export const changeAttendanceStatus = (status: number) => {
    switch (status) {
        case 0:
            return "결석";
        case 1:
            return "출석";
        case 2:
            return "인정 결석";
        case 3:
            return "병결";
        case 4:
            return "사고";
        case 5:
            return "조퇴";
        default:
            return "";
    }
};
// 출석부(attendance) - 인정결석 타입
export const AttendanceAdmitAbsentType = [
    { value: "reason_disease", label: "질병/부상" },
    { value: "reason_disaster", label: "자연재해/재난" },
    { value: "reason_monitoring", label: "모니터링 대상" },
    { value: "reason_family_event", label: "경조사" },
    { value: "reason_etc", label: "기타" },
];

// 수납(payment) - 수납 유형
export const PaymentDetailType = [
    { value: 1, label: "원비" },
    { value: 2, label: "준비물" },
    { value: 3, label: "기타" },
];
// 수납(payment) - 수납 할인 적용 유무(0:사용 안함, 1:사용함)
export const ReceiptDiscount = [
    { value: 0, label: "미적용" },
    { value: 1, label: "적용" },
];
// 수납(payment) - 결제 수단(1:카드, 2:현금, 3:계좌이체, 4:휴대폰, 5:기타)
export const PayMethodType = [
    { value: 1, label: "카드결제" },
    { value: 2, label: "현금결제" },
    { value: 3, label: "계좌이체" },
    { value: 4, label: "휴대폰" },
    { value: 5, label: "기타" },
];
// 수납(payment) - 수납 구분(1:월납, 2:일시납)
export const ReceiptPaymentType = [
    { value: 1, label: "월납" },
    { value: 2, label: "일시납" },
];
export const changeReceiptPaymentType = (type: number) => {
    switch (type) {
        case 1:
            return "월납";
        case 2:
            return "일시납";
        default:
            return "";
    }
};
// 수납(payment) - 할인/프로모션 종류(0:할인율, 1:할인가)
export const PromotionType = [
    { value: 1, label: "할인율(%)" },
    { value: 2, label: "할인가(원)" },
];
// 수납(payment) - 사용 여부(0: 미사용, 1: 사용)
export const IsDisabledType = [
    { value: 0, label: "사용" },
    { value: 1, label: "미사용" },
];
// 수납(payment) - 결제 상태
export const PayStatusType = [
    { value: 0, label: "수납대기" },
    { value: 1, label: "수납요청" },
    { value: 2, label: "수납완료" },
    { value: 3, label: "미납" },
    { value: 4, label: "취소요청" },
    { value: 5, label: "취소완료" },
    { value: 6, label: "환불완료" },
];
// 보호자 관계
export const relationWithStudent = [
    { value: 1, label: "엄마" },
    { value: 2, label: "아빠" },
    { value: 3, label: "할머니" },
    { value: 4, label: "할아버지" },
    { value: 5, label: "이모" },
    { value: 6, label: "삼촌" },
    { value: 7, label: "직접 입력" },
];
export const changeRelationWithStudentType = (type: string) => {
    switch (type) {
        case "": return 0;
        case "엄마": return 1;
        case "아빠": return 2;
        case "할머니": return 3;
        case "할아버지": return 4;
        case "이모": return 5;
        case "삼촌": return 6;
        default:
            return 7;
    }
};

// ****************************************** type 관련 *********************************
// ****************************************** text(기본 또는 데이터 없는 경우) *********************************


// 회원가입 시 문구
export const signTitleText = [
    { title1: "아이스쿨(AI SCHOOL)", title2: "서비스 이용에 동의해 주세요" },
    { title1: "회원 가입을 위해", title2: "본인 인증해 주세요" },
    { title1: "문자로 받은 네자리", title2: "인증번호를 입력해 주세요" },
    { title1: "사용할 아이디와", title2: "비밀번호를 입력해 주세요" },
    { title1: "", title2: "무슨 반을 담당 하시나요?" },
];

// 공통 TEXT
export const NODATATEXT = {
    noSign: [{ index: 1, text: "새로운 요청이 없습니다." }],
    noSite: [{ index: 1, text: "검색된 학교가 없습니다." }],
};
export const error501 =
  "사이트 정보가 잘못되었습니다. 관리자에게 문의해 주시기 바랍니다."; // 잘못된 인자 값입니다.

// NoData text
// 반(class) 없음
export const NoTeamClassDataText = [
    { index: 1, text: "지정된 반이 없어요." },
    { index: 2, text: "반이 지정될 수 있도록 관리자에게 문의해주세요!" },
];
export const NoClassDataText = [
    { index: 1, text: "아직 만들어진 반이 없어요." },
    { index: 2, text: "새로운 반을 생성해 보세요!" },
];
// 학생(student) 없음
export const NoStudentDataText = [
    { index: 1, text: "아직 등록된 학생이 없어요." },
];
// 선생님(user) 없음
export const NoTeacherDataText = [
    { index: 1, text: "아직 등록된 선생님이 없어요." },
];
// 학부모(user) 없음
export const NoParentDataText = [
    { index: 1, text: "아직 등록된 학부모님이 없어요." },
];
// 단말기(device) 없음
export const NoDeviceDataText = [
    { index: 1, text: `아직 등록된 기기가 없어요.` },
    { index: 2, text: `상단 "신규 기기 등록" 버튼을 눌러 추가해보세요.` }
];
// 알림장(notebook) 없음
export const NoNotebookDataToParentText = [
    { index: 1, text: "아직 등록된 알림장이 없어요." },
    { index: 2, text: "새로운 알림장을 등록해 볼까요?" },
];
export const NoNotebookDataToTeacherText = [
    { index: 1, text: "아직 작성된 알림장이 없어요." }
];

// ****************************************** text(기본 또는 데이터 없는 경우) *********************************
// ****************************************** 함수 *********************************

// select-option
/**
 * value = 0(전체)가 있는 select-option 만들기
 * @param data - []
 * @param defaultText value = 0에 대한 text
 * @returns []
 */
export const formattedSelectOption = (data: any, defaultText?: string) => {
    const defaultLabel = defaultText ? defaultText : "전체"
    const newArray: any[] = [{ label: defaultLabel, value: 0 }];
    data.forEach((d: any) => {
        let option = { label: ellipsisText(d.label, 9), value: d.value };
        newArray.push(option);
    });
    return newArray;
}
/**
 * value = 0(전체)가 없는 select-option 만들기
 * @param data 
 * @returns []
 */
export const formattedSelectOptionNotAll = (data: any) => {
    const newArray: any[] = [];
    data.forEach((d: any) => {
        let option = { label: ellipsisText(d.label, 9), value: d.value };
        newArray.push(option);
    });
    return newArray;
}

/**
 * 권한에 따라서 class의 select-option 생성성
 * @param classList 
 * @param user_role 
 * @returns 
 */
export const userClassSelectOption = (classList: ClassInfo[], user_role: string, text?: string) => {
    const defaultText = !text ? "전체" : text;

    // 클래스가 없는 경우 기본값 반환
    if (!classList || classList.length === 0) {
        return formattedSelectOption([], `${user_role === ROLE_DIRECTOR ? defaultText : "담당반 없음"}`);
    }

    // 옵션 데이터 형식으로 변환
    const options = classList.map((d) => ({ 
        label: d.name, 
        value: d.id 
    }));

    // 사용자 역할에 따른 분기 처리
    if (user_role === ROLE_DIRECTOR) {
        return formattedSelectOption(options, defaultText);
    } else if (user_role === ROLE_TEACHER) {
        if (classList.length > 0) {
            return formattedSelectOptionNotAll(options);
        } else {
            return formattedSelectOption([], "담당반 없음");
        }
    }

    // 기본값 반환
    return formattedSelectOption([], `${user_role === ROLE_DIRECTOR ? defaultText : "담당반 없음"}`);
}

/**
 * 학생리스트 select-option
 * @param classList 
 * @param classId 
 * @param user_role 
 * @returns 
 */
export const userClassStudentSelectOption = (classList: ClassInfo[], classId: number, user_role?: string) => {

    let studentList: StudentInfo[];
    if (classId !== -1) {
        studentList = classList.find((c: ClassInfo) => c.id === classId)?.student_list || [];
    }
    else studentList = classList[0].student_list;

    const studentListOption = studentList.length
        ? formattedSelectOption(studentList.map((s: StudentInfo) => ({ label: aesCBCDecrypt(s.name), value: s.id })), "모두보기") : [];
    
    return studentListOption;
}

/**
 * 이름 + 핸드폰 번호에 대한 select-option
 * @param data 
 * @returns []
 */
export const formattedSelectOptionNamePhone = (data: any) => {
    const newArray: any[] = [];
    data.forEach((d: any) => {
        let option = { label: `${ellipsisText(d.name, 6)}(${d.phone})`, value: d.value };
        newArray.push(option);
    });
    return newArray;
}

// 서버 API promise 결과 체크
/**
 * 서버 API의 promise만을 체크하는 함수. (다른 promise는 promise.value의 값이 다를 수 있으니 주의.)
 * @param promise 
 * @returns 
 */
export const checkPromiseOfServer = (promise: any) => {
    if (promise.status === 'fulfilled') {
        if (promise.value.code === 200) {
            return 'S'; // API 호출 성공 & API 결과 성공. success
        }
        return 'F'; // API 호출 성공 & API 결과 실패. fail
    }
    return 'E'; // API 호출 실패. error
};

// File 관련 함수
// 업로드시 제외할 파일 확장자 패턴
export const patternExclusionFile =
    '(ADE|ADP|ASP|ASPX|BAT|CHM|CMD|COM|CPL|DLL|EXE|HTA|INS|ISP|JAR|JS|JSE|JSP|LIB|LNK|MDE|MSC|MSP|MST|PIF|PHP|SCR|SCT|SHB|SYS|VB|VBE|VBS|VXD|WSC|WSF|WSH)$';
// 이미지 파일 확장자 패턴
export const patternImageFile = '(JPG|JPEG|PNG|GIF|JPS|JFIF)$';
// 문서 파일 확장자 패턴
export const patternDocFile = '(PDF|PPTX|DOCX|XLSX|XLS)';
/**
 * 주어진 파일 리스트를 Base64 문자열 배열로 변환
 * @param files - 변환할 파일 리스트
 * @returns Promise<string[]> - Base64 문자열로 변환된 파일 배열
 */
export const convertFilesToBase64 = async (files: FileList): Promise<string[]> => {

    let filesArray = []; // 변환할 파일들을 저장할 배열
    
    // file type 확인
    for (let j = 0; j < files.length; j++) {
            
        // 이미지 확장자 포함 여부 확인
        const fileMimeType = files[j].name.substring(files[j].name.lastIndexOf('.'));
        // 이미지 파일과 제외할 파일 패턴 정의
        const passRegex = new RegExp(patternImageFile, 'i');
        const notPassRegex = new RegExp(patternExclusionFile, 'i');

        // 파일이 이미지 파일인지 및 제외할 파일인지 확인
        const isOkExclude = passRegex.test(fileMimeType);
        const notOkExclude = notPassRegex.test(fileMimeType);

        // 이미지 파일이면서 제외할 파일이 아닐 경우, 배열에 추가
        if (isOkExclude && !notOkExclude) filesArray.push(files[j]);
    }
    // 파일 배열을 Base64로 변환하고 결과를 반환
    return await Promise.all(filesArray.map((file) => fileReader(file)));
};

/**
 * 이미지 업로드 이벤트 처리 및 선택된 파일을 Base64로 변환(convertFilesToBase64())
 * @param event - 파일 업로드 이벤트
 * @returns Promise<string[]> | void - 변환된 Base64 파일 배열 또는 아무것도 반환하지 않음
 */
export const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileArr = event.target.files; // 업로드된 파일 리스트
    if (!fileArr) return;

    try {
        // 파일 리스트를 Base64로 변환
        const base64Files:string[] = await convertFilesToBase64(fileArr);
        return base64Files;

    } catch (error) {
        console.error('Error converting files to base64:', error);
    }
};

/**
 * 주어진 파일을 읽어 Base64 문자열로 변환
 * @param file - 변환할 파일
 * @returns Promise<string> - Base64로 변환된 파일 문자열
 */
export const fileReader = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader(); // FileReader 인스턴스 생성
        reader.readAsDataURL(file); // 파일을 Data URL 형식으로 읽기

        // 파일 읽기 완료 시 호출되는 콜백
        reader.onload = () => {
        if (typeof reader.result === 'string') {
            resolve(reader.result);
        } else {
            reject(new Error('Failed to convert file to base64.'));
        }
        };

        reader.onerror = (error) => reject(error);
    });
};

// 이미지 관련
/**
 * 알림장 이미지 URL 가져오기
 * @param imageData 
 * @returns 
 */
export const getNotebookImageUrl = async (imageData: ImageListType[]) => {

    const urlPromises = imageData.length > 0 ? imageData.map(async (p) => {return getImageUrl(p.file_name)}) : NoImage;

    return await Promise.all(urlPromises);
}

/**
 * 학생 이미지 URL 가져오기 (image_list에 0번째 인덱스)
 * @param studentList 
 * @returns 
 */
export const getStudentImageUrl = async (studentList:StudentInfo[]) => {

    const studentsWithImagePromises = studentList.map(async (student) => {
        // file_size 체크 -> file_size가 0인 경우는 기본 이미지로 보이게 설정
        student.image_url =
            (student.image_list.length > 0 && student.image_list[0].file_size > 0) ? getImageUrl(student.image_list[0].file_name) : DefaultProfileS;
        return {...student};
    });

    return await Promise.all(studentsWithImagePromises);
}

/**
 * 사용자 이미지 URL 가져오기 (image_list에 0번째 인덱스)
 * @param userList 
 * @returns 
 */
export const getUserImageUrl = async (userList:any[]) => {

    const usersWithImagePromises = userList.map(async (user) => {
        // file_size 체크 -> file_size가 0인 경우는 기본 이미지로 보이게 설정
        user.image_url = (user.image_list.length > 0 && user.image_list[0].file_size > 0) ? getImageUrl(user.image_list[0].file_name) : DefaultProfileS;
        return {...user};
    });

    return await Promise.all(usersWithImagePromises);
}

/**
 * default image 설정  - error 발생
 * @param e 
 */
export const onErrorImg = (e: React.SyntheticEvent<HTMLImageElement>) => {
    // 아래코드 무한 로딩 발생으로 미사용
    // e.target.src = DefaultProfileS;

    // 아래코드는 실행 안해봄(ai 답변 코드임)
    // 현재 이미지의 src가 기본 이미지가 아닐 경우에만 변경
    // if (e.currentTarget.src !== DefaultProfileS) {
    //     e.currentTarget.src = DefaultProfileS; // 기본 이미지로 설정
    // }
}

// 단위 표시
/**
 * 숫자 단위 표시(1000 -> 1,000)
 * @param number 
 * @returns 
 */
export const formatCurrency = (number: number | string) => {
    // return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return number.toLocaleString('ko-KR', {maximumFractionDigits: 0});
};

// 용량 관련
/**
 * 숫자를 용량으로 변환
 * - ex) 1024 -> 1MB
 * @param sizes 
 * @returns string
 */
export const getByteSize = (sizes: number[]) => {

    if (!sizes.length) return;

    const byteUnits = ["KB", "MB", "GB", "TB"];

    const size = sizes.reduce((acc, cur) => acc + cur);
    let totalSize = size;

    for (let i = 0; i < byteUnits.length; i++) {
        totalSize = totalSize / 1024;
        
        if (totalSize < 1024) return totalSize.toFixed(2) + byteUnits[i];
    };
};

/**
 * 파일 용량 숫자 부분만 추출하는 함수
 * - ex) 1MB -> 1024
 * @param size 
 * @returns 
 */
export const extractNumber = (size: string) => {
    const byteUnits = ["KB", "MB", "GB", "TB"];

    let extractSize = 0;
    for (let i = 0; i < byteUnits.length; i++) {
        const byteType = size.includes(byteUnits[i]);

        if (byteType) {
            const match = size.match(/[\d.]+/);
            extractSize = match ? parseFloat(match[0]) : 0;

            extractSize = extractSize * 1024 ** (i + 1);
        }
        
    }
    return extractSize;
};

// 엑셀 관련
/**
 * 주어진 문자가 완성형 한글인지 확인
 * @param char 
 * @returns boolean - 완성형 한글이면 true, 아니면 false
 */
const isKoreanCompleteChar = (char: string): boolean => {
  const code = char.charCodeAt(0); // 문자의 유니코드 값
  return code >= 0xac00 && code <= 0xd7a3;
};

/**
 * 한글 단어의 완성형 여부 확인
 * @param word 
 * @returns 
 */
export const checkKoreanWord = (word: string): boolean => {
    // 단어를 문자로 나눈 후, 모든 문자가 완성형 한글인지 확인
    return word.split('').every((char) => isKoreanCompleteChar(char));
};

const CHO = [
  'ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ',
];
const JUNG = [
  'ㅏ', 'ㅐ', 'ㅑ', 'ㅒ', 'ㅓ', 'ㅔ', 'ㅕ', 'ㅖ', 'ㅗ', 'ㅘ', 'ㅙ', 'ㅚ', 'ㅛ', 'ㅜ', 'ㅝ', 'ㅞ', 'ㅟ', 'ㅠ', 'ㅡ', 'ㅢ', 'ㅣ',
];
const JONG = [
  '', 'ㄱ', 'ㄲ', 'ㄳ', 'ㄴ', 'ㄵ', 'ㄶ', 'ㄷ', 'ㄹ', 'ㄺ', 'ㄻ', 'ㄼ', 'ㄽ', 'ㄾ', 'ㄿ', 'ㅀ', 'ㅁ', 'ㅂ', 'ㅄ', 'ㅅ', 'ㅆ',
  'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ',
];

/**
 * 
 * @param components 
 * @returns 
 */
export function assembleHangul(components: string): string {
    const result: string[] = [];
    let index = 0;

    while (index < components.length) {
        const cho = components[index]; // 초성
        const jung = components[index + 1]; // 중성
        let jong = ''; // 종성은 기본값을 빈 문자열로 설정

        if (index + 2 < components.length && JONG.includes(components[index + 2])) {
            jong = components[index + 2]; // 종성이 있으면 할당
            index += 3; // 초성, 중성, 종성 사용했으므로 3칸 이동
        } else {
            index += 2; // 초성, 중성만 사용했으므로 2칸 이동
        }

        const choIndex = CHO.indexOf(cho);
        const jungIndex = JUNG.indexOf(jung);
        const jongIndex = JONG.indexOf(jong);

        if (choIndex === -1 || jungIndex === -1 || jongIndex === -1) {
            throw new Error(`Invalid Hangul components: ${cho}, ${jung}, ${jong}`);
        }

        // 유니코드 계산: (초성 * 588) + (중성 * 28) + 종성 + 0xAC00
        const hangulCode = 0xac00 + choIndex * 588 + jungIndex * 28 + jongIndex;
        result.push(String.fromCharCode(hangulCode));
    }

    return result.join('');
}

// text 관련
/**
 * 텍스트 말줄임 붙이는 함수
 * - 원하는 길이 이후에 말줄임(...)을 붙여줌
 * @param text 
 * @param length 
 * @returns 
 */
export const ellipsisText = (text: string, length: number) => {
  return text.length > length ? text.slice(0, length) + "..." : text;
};

// DatePicker 관련 함수
/**
 * DatePicker에서 선택한 날/공휴일/주말 체크용
 * @param date 
 * @param startDate 
 * @param holidayList 
 * @returns 
 */
export const getDayClassName = (date: Date, startDate: Date, holidayList: string[]) => {

    const formatDate = checkDateAndFormattedDate(date); // yyyy-mm-dd
    const selectDate = date.getDate();
    const day = date.getDay();
    let className = '';


    // 날짜 선택 체크
    if (selectDate === startDate.getDate()) {
        className += 'selectedDay';
    }
    else className += 'unSelectedDay';
  
    // 주말 체크
    if (day === 0) {
        className += ' holiday_sun';
    }
    else if (day === 6) {
        className += ' holiday_sat';
    }

    // 공휴일 체크
    if (holidayList.some((day) => day === formatDate)) {
        className += ' holiday';
    }
    
    return className;
}

// 마스킹
/**
 * 핸드폰 번호 마스킹
 * - 010-1**4-5678
 * @param phoneNumber 
 */
export const maskedPhoneNumber = (phoneNumber: string) => {
    if (phoneNumber.length !== 11) {
        console.error("휴대폰 번호는 '-' 없이 11자리여야합니다.");
        return phoneNumber;
    }

    const maskedPart = `${phoneNumber[3]}**${phoneNumber[6]}`;
    const maskPhoneNumber = `${phoneNumber.substring(0, 3)}-${maskedPart}-${phoneNumber.substring(8)}`;
    return maskPhoneNumber;
}

// ****************************************** 함수 *********************************
// ****************************************** CSS *********************************

// 다음주소 팝업창 theme
export const themeObj = {
    bgColor: "#162525", //바탕 배경색
    searchBgColor: "#162525", //검색창 배경색
    contentBgColor: "#162525", //본문 배경색(검색결과,결과없음,첫화면,검색서제스트)
    pageBgColor: "#162525", //페이지 배경색
    textColor: "#FFFFFF", //기본 글자색
    queryTextColor: "#FFFFFF", //검색창 글자색
    outlineColor: "#444444", //테두리
};

// ****************************************** CSS *********************************